import React from 'react';
import { CheckCircleIcon, PlayIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
interface IntroScreenProps {
  onBegin: () => void;
}
export function IntroScreen({
  onBegin
}: IntroScreenProps) {
  return <div className="min-h-screen bg-white pb-20" data-id="element-44">
      {/* Hero section */}
      <div className="bg-gradient-to-br from-roche-blue to-roche-blue-dark text-white px-6 py-12" data-id="element-45">
        <div className="flex items-center gap-3 mb-6" data-id="element-46">
          <PlayIcon className="w-8 h-8" aria-hidden="true" data-id="element-47" />
          <h1 className="text-3xl font-bold" data-id="element-48">Storyboard Review</h1>
        </div>
        <p className="text-lg opacity-90 leading-relaxed" data-id="element-49">
          Interactive educational journey for healthcare professionals
        </p>
      </div>

      {/* Review card */}
      <div className="px-6 py-8" data-id="element-50">
        <div className="bg-white border border-border-gray rounded-lg p-6 shadow-sm" data-id="element-51">
          <h2 className="text-xl font-bold text-text-primary mb-6" data-id="element-52">
            Campaign Details
          </h2>

          <div className="space-y-4" data-id="element-53">
            <div className="grid grid-cols-2 gap-4" data-id="element-54">
              <div data-id="element-55">
                <p className="text-xs text-text-secondary mb-1" data-id="element-56">Company</p>
                <p className="font-semibold text-text-primary" data-id="element-57">ROCHE</p>
              </div>
              <div data-id="element-58">
                <p className="text-xs text-text-secondary mb-1" data-id="element-59">Country</p>
                <p className="font-semibold text-text-primary" data-id="element-60">Ivory Coast</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-61">
              <div data-id="element-62">
                <p className="text-xs text-text-secondary mb-1" data-id="element-63">Brand</p>
                <p className="font-semibold text-text-primary" data-id="element-64">Vabysmo</p>
              </div>
              <div data-id="element-65">
                <p className="text-xs text-text-secondary mb-1" data-id="element-66">Type</p>
                <p className="font-semibold text-text-primary" data-id="element-67">Trailer</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-68">
              <div data-id="element-69">
                <p className="text-xs text-text-secondary mb-1" data-id="element-70">Canvas</p>
                <p className="font-semibold text-text-primary" data-id="element-71">Mobile 9:16</p>
              </div>
              <div data-id="element-72">
                <p className="text-xs text-text-secondary mb-1" data-id="element-73">Version</p>
                <p className="font-semibold text-text-primary" data-id="element-74">2.0</p>
              </div>
            </div>

            <div data-id="element-75">
              <p className="text-xs text-text-secondary mb-1" data-id="element-76">Date</p>
              <p className="font-semibold text-text-primary" data-id="element-77">February 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign overview */}
      <div className="px-6 py-4" data-id="element-78">
        <h3 className="text-lg font-bold text-text-primary mb-4" data-id="element-79">
          Campaign Overview
        </h3>

        <div className="space-y-3" data-id="element-80">
          <div className="flex items-start gap-3" data-id="element-81">
            <div className="w-2 h-2 rounded-full bg-roche-blue mt-2 flex-shrink-0" data-id="element-82" />
            <div data-id="element-83">
              <p className="font-semibold text-text-primary" data-id="element-84">Objective</p>
              <p className="text-sm text-text-secondary" data-id="element-85">Awareness/Education</p>
            </div>
          </div>

          <div className="flex items-start gap-3" data-id="element-86">
            <div className="w-2 h-2 rounded-full bg-roche-blue mt-2 flex-shrink-0" data-id="element-87" />
            <div data-id="element-88">
              <p className="font-semibold text-text-primary" data-id="element-89">Audience</p>
              <p className="text-sm text-text-secondary" data-id="element-90">
                Healthcare Professionals
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3" data-id="element-91">
            <div className="w-2 h-2 rounded-full bg-roche-blue mt-2 flex-shrink-0" data-id="element-92" />
            <div data-id="element-93">
              <p className="font-semibold text-text-primary" data-id="element-94">Go-Live</p>
              <p className="text-sm text-text-secondary" data-id="element-95">Q1 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Review checklist */}
      <div className="px-6 py-4" data-id="element-96">
        <h3 className="text-lg font-bold text-text-primary mb-4" data-id="element-97">
          What We Need You to Review
        </h3>

        <div className="space-y-3" data-id="element-98">
          <div className="flex items-start gap-3" data-id="element-99">
            <CheckCircleIcon className="w-5 h-5 text-roche-blue mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-100" />
            <p className="text-text-primary" data-id="element-101">
              Narrative flow and topic progression
            </p>
          </div>

          <div className="flex items-start gap-3" data-id="element-102">
            <CheckCircleIcon className="w-5 h-5 text-roche-blue mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-103" />
            <p className="text-text-primary" data-id="element-104">
              FABP backbone (Features, Advantages, Benefits, Proof)
            </p>
          </div>

          <div className="flex items-start gap-3" data-id="element-105">
            <CheckCircleIcon className="w-5 h-5 text-roche-blue mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-106" />
            <p className="text-text-primary" data-id="element-107">
              Interactive elements and engagement
            </p>
          </div>

          <div className="flex items-start gap-3" data-id="element-108">
            <CheckCircleIcon className="w-5 h-5 text-roche-blue mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-109" />
            <p className="text-text-primary" data-id="element-110">
              Visual direction and brand alignment
            </p>
          </div>

          <div className="flex items-start gap-3" data-id="element-111">
            <CheckCircleIcon className="w-5 h-5 text-roche-blue mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-112" />
            <p className="text-text-primary" data-id="element-113">
              MLR flags and compliance considerations
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <CTAButton onClick={onBegin} data-id="element-114">Begin Journey Review</CTAButton>
    </div>;
}